// One example payload, told event by event — the data behind the payload
// events explorer (PayloadWebhookEventsExplorer), and the exact resources the
// "send a test event" action POSTs at a sandbox endpoint.
//
// The resources are the service's own published examples
// (services/curri-integrations-service/src/surfaces/rest/examples.ts) and the
// envelope is what its consumer builds around them (webhooks/consumer/render.ts),
// with the field order the partner mapper emits — and the test-send endpoint
// validates the body against `payloadSchema`, so drift here fails loudly rather
// than sending something the docs never showed.

const ACCOUNT_ID = 'acct_EXAMPLE001'

const PICKUP = {
  address: {
    addressLine1: '4341 W Van Buren St',
    city: 'Chicago',
    state: 'IL',
    postalCode: '60624',
  },
  contact: {
    company: 'Buenaventura Supply Co.',
    name: 'John Smith',
    phoneNumber: '+18055550100',
    emailAddress: 'jsmith@example.com',
  },
  timeWindow: {
    start: '2026-07-14T20:00:00.000Z',
    end: '2026-07-14T22:00:00.000Z',
  },
  notes: 'Drive to the front of the store',
}

const DROPOFF = {
  coordinates: { latitude: 41.8781, longitude: -87.6847 },
  contact: {
    name: 'Jane Smith',
    phoneNumber: '+18055550101',
    emailAddress: 'jane@example.com',
  },
  notes: 'This is in a new construction',
}

/** Imperial, because a response reports what Curri stored rather than what was sent. */
const ITEMS = [
  {
    description: 'Box of tile',
    lengthIn: 24,
    widthIn: 24,
    heightIn: 12,
    weightLbs: 43.9,
  },
]

const BOL_ONLY = { bolNumber: '2349' }
const WITH_PO = { poNumber: 'PO-99871', bolNumber: '2349' }

const CANCELED_STATUS = {
  code: 'failed',
  reasonCode: 'customer_canceled',
  note: 'Customer no longer needs this delivery',
}

interface EventMoment {
  /** One line under the event name in the picker. */
  description: string
  /** One sentence about what changed on the payload at this moment. */
  caption: string
  status: { code: string; reasonCode?: string; note?: string }
  referenceNumbers: Record<string, string>
}

const EVENTS = {
  'payload.created': {
    description: 'Curri recorded a new payload',
    caption:
      'A payload was created. Curri holds a record of the freight and nothing is acting on it, which is what `draft` says — it claims nothing about whether the payload is complete.',
    status: { code: 'draft' },
    referenceNumbers: BOL_ONLY,
  },
  'payload.updated': {
    description: 'A field was amended',
    caption:
      'An amendment landed — this one added a PO number. The whole resource travels, not the change, so apply it as a replacement rather than a patch.',
    status: { code: 'draft' },
    referenceNumbers: WITH_PO,
  },
  'payload.status.scheduled': {
    description: 'The payload reached scheduled',
    caption:
      'A status change, named by the status the payload reached — the type and `data.payload.status.code` always agree, so branch on either.',
    status: { code: 'scheduled' },
    referenceNumbers: BOL_ONLY,
  },
  'payload.status.failed': {
    description: 'Terminal — the payload failed',
    caption:
      'Terminal: `failed` is the only negative status. `reasonCode` separates your own withdrawal (`customer_canceled`) from an operational failure, and `note` is the sentence attached to it.',
    status: CANCELED_STATUS,
    referenceNumbers: BOL_ONLY,
  },
  'payload.status.restated': {
    description: 'The reason changed, the status did not',
    caption:
      'The same `failed` status with a corrected note. The code has not moved, so a handler that acted on reaching `failed` must not act again.',
    status: {
      ...CANCELED_STATUS,
      note: 'Customer no longer needs this delivery — duplicate of SO-4471',
    },
    referenceNumbers: BOL_ONLY,
  },
} satisfies Record<string, EventMoment>

export type ExampleEvent = keyof typeof EVENTS

export const EXAMPLE_EVENTS = Object.keys(EVENTS) as ExampleEvent[]

export const momentFor = (event: ExampleEvent): EventMoment => EVENTS[event]

/**
 * The payload at an event, as the object the test-send endpoint accepts. Key
 * order matches the partner mapper's object literal
 * (surfaces/rest/payload-mapping.ts), so the example diffs cleanly against a
 * body a real event carried.
 */
export const examplePayloadFor = (
  event: ExampleEvent
): Record<string, unknown> => {
  const moment = EVENTS[event]
  return {
    id: 'payload_7KQ4M2XJ9D',
    status: moment.status,
    accountId: ACCOUNT_ID,
    customerData: { orderNumber: '234' },
    referenceNumbers: moment.referenceNumbers,
    pickup: PICKUP,
    dropoff: DROPOFF,
    items: ITEMS,
    createdAt: '2026-07-13T10:02:11.000Z',
  }
}

/** The id Curri minted for this event, and the dedup key on the wire. */
const EVENT_ID = 'd9a1c4f2-6b3e-4a7c-9f18-2e5b7c0d1a44'
const CREATED_AT = '2026-07-14T18:31:07.212Z'
const API_VERSION = '2026-07-15'

export const exampleEnvelopeFor = (
  event: ExampleEvent
): Record<string, unknown> => ({
  id: EVENT_ID,
  type: event,
  createdAt: CREATED_AT,
  apiVersion: API_VERSION,
  isSandbox: true,
  source: 'partner',
  data: { payload: examplePayloadFor(event) },
})

/**
 * The headers that envelope goes out with. The signature is a placeholder: it
 * is an HMAC over `<timestamp>.<body>` keyed with the reader's own endpoint
 * secret, so no value printed here would verify against anything they hold.
 */
export const exampleHeadersFor = (
  event: ExampleEvent
): Record<string, string> => ({
  'Content-Type': 'application/json; charset=utf-8',
  'User-Agent': 'Curri-Webhooks/1 (+https://integrations.curri.com/docs/webhooks)',
  'Curri-Webhook-Id': EVENT_ID,
  'Curri-Webhook-Timestamp': String(Math.floor(Date.parse(CREATED_AT) / 1000)),
  'Curri-Event-Type': event,
  'Curri-Version': API_VERSION,
  'Curri-Signature': 'v1=<hex>',
})

export const exampleEnvelopeJsonFor = (event: ExampleEvent): string =>
  JSON.stringify(exampleEnvelopeFor(event), null, 2)
